import { cva, type VariantProps } from 'class-variance-authority';

export const radioItemVariants = cva('flex items-center font-normal', {
  variants: {
    appearance: {
      default: 'gap-xxs text-l1 text-body',
      card: [
        'w-full gap-md rounded-lg border bg-white p-md',
        'has-[:focus-visible]:outline-none',
        'has-[:focus-visible]:ring-2',
        'has-[:focus-visible]:ring-primary-700',
        'has-[:focus-visible]:ring-offset-2',
      ].join(' '),
    },
    controlPosition: {
      start: '',
      end: '',
    },
    error: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      appearance: 'card',
      error: false,
      className: [
        'border-neutral-200',
        'has-[[data-state=checked]]:border-neutral-900',
      ].join(' '),
    },
    {
      appearance: 'card',
      error: true,
      className: 'border-error-500',
    },
    {
      appearance: 'default',
      error: true,
      className: 'text-error-500',
    },
    {
      appearance: 'card',
      controlPosition: 'end',
      className: 'justify-between',
    },
  ],
  defaultVariants: {
    appearance: 'default',
    controlPosition: 'start',
    error: false,
  },
});

export type RadioItemVariantProps = VariantProps<typeof radioItemVariants>;

/** Circle treatment used only when the group appearance is card. */
export const radioCardControlClassName = [
  'size-5',
  'hover:border-neutral-200',
  'hover:ring-0',
  'focus:border-neutral-200',
  'focus:ring-0',
].join(' ');

/** Selected card circle is a dark ring with a white center. */
export const radioCardSelectedControlClassName = [
  'data-[state=checked]:border-radio-card-selected',
  'data-[state=checked]:border-neutral-900',
  'data-[state=checked]:bg-white',
  'data-[state=checked]:hover:border-neutral-900',
  'data-[state=checked]:hover:bg-white',
  'data-[state=checked]:focus:border-neutral-900',
  '[&[data-state=checked]_svg]:hidden',
].join(' ');

/** Error card circle is a thin red ring with a white center. */
export const radioCardErrorControlClassName = [
  'size-5',
  'border-error-500',
  'bg-white',
  'hover:border-error-500',
  'hover:ring-0',
  'aria-[invalid=true]:hover:ring-0',
  'focus:border-error-500',
  'focus:ring-0',
  'aria-[invalid=true]:focus:ring-0',
  '[&_svg]:hidden',
].join(' ');

/** Selected error card circle keeps the dark ring and adds a light red halo. */
export const radioCardErrorSelectedControlClassName = [
  'data-[state=checked]:border-radio-card-selected',
  'data-[state=checked]:border-neutral-900',
  'data-[state=checked]:bg-white',
  'data-[state=checked]:hover:border-neutral-900',
  'data-[state=checked]:focus:border-neutral-900',
  'data-[state=checked]:ring-2',
  'data-[state=checked]:ring-error-300',
  'data-[state=checked]:hover:ring-2',
  'data-[state=checked]:hover:ring-error-300',
  'data-[state=checked]:focus:ring-2',
  'data-[state=checked]:focus:ring-error-300',
].join(' ');
